import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { TextAreaControl } from 'src/editor/shared/settings/controls/text-area-control'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { ModalWindow } from 'src/editor/shared/ui-lib/modal-window'
import { generatePathFromText } from 'src/editor/shared/utils/generate-path-from-text'
import { sanitizeUrlInput } from 'src/editor/shared/utils/sanitize-url-input'
import { BlogCategoryFormModeEnum, BlogCategoryFormValues } from '../types'

interface CreateEditCategoryModalProps {
  isOpened: boolean
  mode: BlogCategoryFormModeEnum
  initialValues?: BlogCategoryFormValues
  onClose: () => void
  onSubmit: (values: BlogCategoryFormValues) => void
}

const emptyCategoryFormValues: BlogCategoryFormValues = {
  name: '',
  path: '',
  description: '',
  seoTitle: '',
  seoDescription: '',
}

export const CreateEditCategoryModal = ({
  isOpened,
  mode,
  initialValues,
  onClose,
  onSubmit,
}: CreateEditCategoryModalProps) => {
  const { t } = useTranslation()

  const [values, setValues] = React.useState<BlogCategoryFormValues>(
    emptyCategoryFormValues,
  )
  const [isPathTouched, setIsPathTouched] = React.useState(false)

  useEffect(() => {
    if (!isOpened) {
      return
    }

    setValues(initialValues ?? emptyCategoryFormValues)
    setIsPathTouched(mode === BlogCategoryFormModeEnum.Edit)
  }, [isOpened, initialValues, mode])

  const updateValue = <Key extends keyof BlogCategoryFormValues>(
    key: Key,
    value: BlogCategoryFormValues[Key],
  ) => {
    setValues(prev => ({ ...prev, [key]: value }))
  }

  const handleNameChange = (name: string) => {
    updateValue('name', name)

    if (!isPathTouched) {
      updateValue('path', sanitizeUrlInput(name))
    }
  }

  const handlePathChange = (path: string) => {
    setIsPathTouched(true)
    updateValue('path', sanitizeUrlInput(path))
  }

  const handlePathBlur = () => {
    updateValue('path', generatePathFromText(values.path))
  }

  const handleSubmit = () => {
    onSubmit(values)
  }

  return (
    <ModalWindow
      isOpened={isOpened}
      onClose={onClose}
      title={
        mode === BlogCategoryFormModeEnum.Create
          ? t('website_settings.sidebar.blog.categories.create_title')
          : t('website_settings.sidebar.blog.categories.edit_title')
      }
      cancelButtonProps={{
        onClick: onClose,
        children: t('global.cancel'),
      }}
      confirmButtonProps={{
        onClick: handleSubmit,
        children: t('core.header.save'),
      }}
      confirmOnEnter
    >
      <TextInputControl
        wrapperProps={{
          className: 'mt-1 mb-3',
          labelText: t('website_settings.sidebar.blog.categories.name'),
          required: true,
        }}
        id="blog-category-name"
        value={values.name}
        update={handleNameChange}
      />

      <TextInputControl
        wrapperProps={{
          className: 'mt-1 mb-3',
          labelText: t('website_settings.sidebar.blog.categories.path'),
          required: true,
        }}
        id="blog-category-path"
        value={values.path}
        update={handlePathChange}
        onBlur={handlePathBlur}
      />

      <TextAreaControl
        wrapperProps={{
          className: 'mt-1 mb-3',
          labelText: t('website_settings.sidebar.blog.categories.description'),
        }}
        id="blog-category-description"
        value={values.description}
        update={value => updateValue('description', value)}
      />

      <TextInputControl
        wrapperProps={{
          className: 'mt-1 mb-3',
          labelText: t('website_settings.sidebar.blog.categories.seo_title'),
        }}
        id="blog-category-seo-title"
        value={values.seoTitle}
        update={value => updateValue('seoTitle', value)}
      />

      <TextAreaControl
        wrapperProps={{
          className: 'mt-1',
          labelText: t(
            'website_settings.sidebar.blog.categories.seo_description',
          ),
        }}
        id="blog-category-seo-description"
        value={values.seoDescription}
        update={value => updateValue('seoDescription', value)}
      />
    </ModalWindow>
  )
}
